import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, List, Modal } from "semantic-ui-react";
import { DatePicker } from "../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import { Designations, RatePricingTypesEnum } from "../../../components/shared/constants";
import moment from "moment";
import NumericInput from "../../../components/shared/NumericInput";
import { getFilteredDates, handleStartEndChange } from "../../../components/shared/utils";
import { PriceListContext } from "../context";
import { onWarning } from "../../../components/shared/messages";
import { useConfirm } from "../../../hooks/useConfirm";
import PricingElementPanel from "../../../components/shared/PricingElementPanel";
import { RPEsRearangeDateRanges, setRPEsWithRearangedExceptions, insertCopiedPricing, areAllStartEndDatesValid, hasZeroPrice } from "./pricingUtils";
import { PriceElementsList } from "./PriceElementsList";

export const PricingPerVillaPopup = ({ open, onClose, EntityTypeId, EntityId, updateRPEs, Packages,
  rateElementName, onDelete, onPricingSave }) => {
  const text = useTextResources();
  const message = useMessageResources();
  const confirm = useConfirm();
  const { state } = useContext(PriceListContext);
  const [pricingElements, setPricingElements] = useState([]);
  const [fromFilterDate, setFromFilterDate] = useState();
  const [toFilterDate, setToFilterDate] = useState();
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().add(1, "day").toDate());
  const [selectedDays, setSelectedDays] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const priceToUpdate = useMemo(() => {
    return state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";
  }, [state?.IsProductPricingIncludesTax]);

  const getVillahElements = useCallback(() => {
    //filter out rateElements that aren't for villa (NumberOfPersonsWithoutExtraCharge is null)
    let RatePricingElements = state.RatePricingElements.filter(rpe =>
      rpe.EntityTypeId === EntityTypeId &&
      rpe.EntityId === EntityId &&
      Boolean(rpe.RatePricingElementsPackages?.length) === Boolean(Packages?.length) &&
      rpe.IsActive);
    return RatePricingElements;
  }, [state?.RatePricingElements, EntityId, EntityTypeId])

  useEffect(() => {
    if (open) {
      let villaPricingElements = getVillahElements();
      const rateElementsWithWeeks = villaPricingElements.map(rpe => {
        let fittingRatePerVilla = rpe.RatePerPeople.find(x => !x.ExceptionDate);

        const nonDesignatedPrice = priceToUpdate === "AmountAfterTax" ? "AmountBeforeTax" : "AmountAfterTax";
        if (fittingRatePerVilla)
          fittingRatePerVilla[nonDesignatedPrice] = 0;

        return {
          ...rpe, RatePerPeople: fittingRatePerVilla ? [fittingRatePerVilla, ...rpe.RatePerPeople.filter(rpp => rpp.ExceptionDate)] : [{
            RatePricingElementId: rpe.Id,
            RatePerPersonPrices: [],
            RateId: state.Id,
            RoomTypeId: EntityId,
          }]
        };
      });
      setPricingElements(JSON.parse(JSON.stringify(rateElementsWithWeeks.sort((a, b) => a.StartDate < b.StartDate ? -1 : 1))));
    } else {
      setPricingElements([]);
      setIsDirty(false);
    }
  }, [state?.RatePricingElements, EntityId, EntityTypeId, open])

  const isValidBeforeSave = async () => {
    let shouldContinue = true;
    if (pricingElements.some(x => x.isEdit)) {
      onWarning(message("ApproveOpenPricingsBeforeSave"));
      return false;
    }

    if (!areAllStartEndDatesValid(pricingElements)) {
      onWarning(message("DatesIsMandatoryField"));
      return false;
    }

    let requiredDesignatedPrices = [Designations.FixBasePrice, Designations.ExtraPerson];
    if (hasZeroPrice(pricingElements, requiredDesignatedPrices, RatePricingTypesEnum.FixPerPax, priceToUpdate)) {
      shouldContinue = await confirm({
        header: message("SomePricesAreZero"),
        content: message("WouldYouLikeToContinue"),
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
      });
    }
    return shouldContinue;
  }

  const onSave = async (isClose) => {
    updateRPEs({ rpes: pricingElements, isClose, setIsDirty, validate: isValidBeforeSave, setPricings: setPricingElements });
  };

  const changePricingField = useCallback((rpe, field, value) => {
    if (!value || isNaN(value)) return;
    setIsDirty(true);
    rpe.IsUpdated = true;
    rpe.RatePerPeople[0][field] = value;
    setPricingElements([...pricingElements]);
  }, [pricingElements])

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  const addPricing = () => {
    const newRpe = {
      TempId: `tempPricingId-${crypto.randomUUID()}`,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      EntityTypeId,
      EntityId,
      RatePricingTypeId: RatePricingTypesEnum.FixPerPax,
      RatePerPeople: [{
        RatePricingElementId: 0,
        RatePerPersonPrices: [],
        RateId: state.Id,
        RoomTypeId: EntityId,
      }],
      RateId: state.Id,
      IsActive: true,
      CrateDate: new Date(),
      IsNew: true
    }

    const newPricings = RPEsRearangeDateRanges(newRpe, pricingElements, state.BaseRateId);
    setRPEsWithRearangedExceptions(newPricings, 'RatePerPeople')
    setPricingElements(newPricings)

    setIsDirty(true);
  };

  const onCopy = (oldPricing, newPricing) => {
    const rpes = insertCopiedPricing(pricingElements, oldPricing, newPricing);
    setPricingElements(rpes);
    setIsDirty(true)
  }

  const onPricingSaveClicked = (pricing) => {
    onPricingSave({ rpe: pricing, rpes: pricingElements, setPricings: setPricingElements, setIsDirty, name: 'RatePerPeople' });
  }

  const getPriceByDesignationId = useCallback((rpe, designationId) => {
    return rpe.RatePerPeople[0].RatePerPersonPrices?.find(x => x.DesignationId === designationId)?.[priceToUpdate];
  }, [pricingElements]);

  const changeDesignatedPrice = useCallback((rpe, designationId, value) => {
    if (!value && value != 0) {
      value = 0;
    }
    setIsDirty(true);
    const ratePerPerson = rpe.RatePerPeople[0];
    const fittingDesignatedPrice = ratePerPerson.RatePerPersonPrices.find(x => x.DesignationId === designationId);
    if (fittingDesignatedPrice)
      fittingDesignatedPrice[priceToUpdate] = value;
    else
      ratePerPerson.RatePerPersonPrices.push({
        Id: 0,
        RatePerPersonId: ratePerPerson.Id,
        DesignationId: designationId,
        AmountAfterTax: 0,
        AmountBeforeTax: 0,
        [priceToUpdate]: value
      })
    setPricingElements([...pricingElements]);
  }, [pricingElements])

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  return (
    <Modal size="large" open={open} dimmer centered className="price-list-settings">
      <Modal.Header>
        {`${text("SetVillaPrice")} - ${rateElementName}`}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths={4} >
            <DatePicker
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("From")}
              onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
              clearable={false}
              value={startDate}
            />
            <DatePicker
              minDate={moment(startDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("To")}
              onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
              clearable={false} value={endDate}
            />
            <div className="weekdays-select">
              <WeekDaysSelect onChange={setSelectedDays} dates={memoDatesForWeekComponent} />
              <div className="add-pricing-button">
                <Button primary onClick={addPricing} disabled={selectedDays?.length === 0}>{text("Add")}</Button>
              </div>
            </div>
          </Form.Group>
          <Form.Group widths={4}>
            <DatePicker
              label={`${text("FilterBy")} ${text("FromDate")}`}
              value={fromFilterDate}
              onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <DatePicker
              label={`${text("FilterBy")} ${text("ToDate")}`}
              value={toFilterDate}
              onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(fromFilterDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
              <Button primary onClick={() => {
                setFromFilterDate();
                setToFilterDate();
              }}>{text("Clear")}</Button>
            </div>
            <Form.Field></Form.Field>
          </Form.Group>
          <PriceElementsList state={state} pricings={pricingElements}>
              {getFilteredDates(pricingElements, fromFilterDate, toFilterDate)?.filter(x => x.IsActive && !x.IsOverride).map((rpe) => {
                const isDerived = rpe.RateId === state.BaseRateId;
                return (
                  <List.Item key={rpe.Id || rpe.TempId}>
                    <PricingElementPanel
                      pricing={rpe}
                      onDelete={() => onDelete(rpe, pricingElements, setPricingElements)}
                      onSave={onPricingSaveClicked}
                      onCopy={onCopy}
                      isDerived={isDerived}
                      setPricings={setPricingElements}
                    >
                      <Form.Group widths="equal">
                        <Form.Field
                          disabled={rpe.RateId === state.BaseRateId}
                          className={rpe.RateId === state.BaseRateId && "property-input-highlighted"}
                          value={getPriceByDesignationId(rpe, Designations.FixBasePrice)}
                          selectOnFocus control={NumericInput} required nullable
                          label={text("FixedPrice")}
                          onChangeComplete={(value) => changeDesignatedPrice(rpe, Designations.FixBasePrice, value)}
                        />
                        <Form.Field
                          disabled={rpe.RateId === state.BaseRateId}
                          className={rpe.RateId === state.BaseRateId && "property-input-highlighted"}
                          value={rpe.RatePerPeople[0]?.NumberOfPersonsWithoutExtraCharge}
                          selectOnFocus control={NumericInput}
                          label={text("Pax")}
                          onChangeComplete={(value) => changePricingField(rpe, "NumberOfPersonsWithoutExtraCharge", value)}
                          digits={0}
                          min={0}
                        />
                        <Form.Field
                          disabled={rpe.RateId === state.BaseRateId}
                          className={rpe.RateId === state.BaseRateId && "property-input-highlighted"}
                          value={getPriceByDesignationId(rpe, Designations.ExtraPerson)}
                          selectOnFocus
                          control={NumericInput}
                          label={text("ExtraPersonCharge")}
                          onChangeComplete={(value) => changeDesignatedPrice(rpe, Designations.ExtraPerson, value)}
                        />
                      </Form.Group>
                    </PricingElementPanel>
                  </List.Item>
                );
              })}
          </PriceElementsList>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={() => onSave(true)}>
          {text("Saveandclose")}
        </Button>
        <Button positive onClick={() => onSave()}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PricingPerVillaPopup.propTypes = {
  EntityTypeId: PropTypes.number,
  EntityId: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateRPEs: PropTypes.func,
  Packages: PropTypes.array,
  rateElementName: PropTypes.string,
  onDelete: PropTypes.func,
  onPricingSave: PropTypes.func
};
